import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import useSubscribe from '@/hooks/useSubscribe';
import type { GsStory } from '@/lib/types';

const FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=Dramatic%20wide%20view%20of%20a%20Southern%20California%20golf%20course%20at%20golden%20hour%20with%20palm%20tree%20silhouettes%2C%20long%20shadows%20across%20a%20deep%20green%20fairway%2C%20dark%20moody%20edges%20for%20text%20contrast%2C%20cinematic%20sports%20broadcast%20style%2C%20no%20people%20no%20logos&width=1920&height=1080&seq=gs-hero-1&orientation=landscape';

export default function Hero() {
  const { data: stories } = useTable<GsStory>('gs_stories');
  const { status, formError, submit } = useSubscribe('weekly-digest', 'company_alt');

  // Featured stories: the lead story first, then the next stories in editorial order (up to 5).
  const featured = useMemo(() => {
    const leadStory = stories.find((s) => s.is_lead) || stories[0];
    if (!leadStory) return [];
    return [leadStory, ...stories.filter((s) => s.id !== leadStory.id)].slice(0, 5);
  }, [stories]);
  const [index, setIndex] = useState(0);
  const count = featured.length;
  const go = useCallback((dir: number) => {
    if (count > 1) setIndex((i) => (i + dir + count) % count);
  }, [count]);
  useEffect(() => { if (index >= count) setIndex(0); }, [count, index]);
  const lead = featured[index];

  // Swipe left or right on touch screens.
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return (
    <section
      id="subscribe"
      className="relative min-h-[720px] md:h-[820px] flex items-end"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      aria-label="Featured stories"
    >
      {count === 0 && (
        <img src={FALLBACK_IMAGE} alt="Southern California golf course at golden hour" className="absolute inset-0 w-full h-full object-cover object-top" />
      )}
      {featured.map((s, i) => (
        <img
          key={s.id}
          src={s.image || FALLBACK_IMAGE}
          alt={i === index ? s.title : ''}
          aria-hidden={i !== index}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 motion-reduce:transition-none ${i === index ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/55 to-foreground-950/30"></div>

      <div className="relative w-full px-4 md:px-8 pb-10 md:pb-16 pt-28">
        {/* Slim masthead line */}
        <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-9 gs-rise">
          <span className="font-heading font-extrabold uppercase text-background-50 text-lg md:text-xl tracking-tight whitespace-nowrap">
            GreenSide<span className="text-accent-500"> News</span>
          </span>
          <span className="gs-hairline flex-1 opacity-70"></span>
          <span className="hidden sm:block font-label uppercase tracking-[0.24em] text-[10px] md:text-xs text-background-200 whitespace-nowrap">
            Local &amp; community golf
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          {/* Featured story */}
          <div className="lg:col-span-8 gs-rise-2">
            <Link to={lead?.slug ? `/news/${lead.slug}` : '/news'} className="group block cursor-pointer">
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 bg-accent-500 text-foreground-950 font-label font-bold uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-sm">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>Featured story
                </span>
                {lead?.category && (
                  <span className="font-label uppercase tracking-widest text-[11px] text-background-100 whitespace-nowrap">
                    {lead.category}
                    {lead.city ? ` \u00b7 ${lead.city}` : ''}
                  </span>
                )}
              </span>

              <h1 className="mt-5 font-heading font-extrabold text-background-50 text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl group-hover:text-accent-100 transition-colors">
                {lead ? lead.title : 'The world of golf'}
              </h1>

              <p className="mt-4 text-background-200 text-sm md:text-base max-w-xl">
                {lead
                  ? lead.excerpt || 'The story leading the front page this week — read it in full.'
                  : 'GreenSide News covers local and community golf — your source of stories, resources, tournaments and all things golf.'}
              </p>

              <span className="mt-5 inline-flex items-center gap-2 font-label uppercase tracking-widest text-xs font-bold text-accent-500 whitespace-nowrap">
                Read the story
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </span>
            </Link>

            {count > 1 && (
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous featured story"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/60 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next featured story"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/60 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
                <div className="flex items-center gap-1.5 ml-2">
                  {featured.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setIndex(i)}
                      aria-label={`Show featured story ${i + 1}: ${s.title}`}
                      aria-current={i === index}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50'}`}
                    />
                  ))}
                </div>
                <span className="ml-auto font-label uppercase tracking-widest text-[11px] text-background-200" aria-live="polite">
                  {index + 1} / {count}
                </span>
              </div>
            )}
          </div>

          {/* Slim newsletter bar */}
          <div className="lg:col-span-4 gs-rise-3">
            <form
              id="weekly-digest-form"
              onSubmit={submit}
              className="bg-background-50/95 backdrop-blur-sm rounded-lg p-4 md:p-5 border-t-4 border-accent-500"
            >
              <p className="font-heading font-bold uppercase text-lg text-foreground-950">Weekly digest, free</p>
              <p className="text-xs text-foreground-600 mt-1">Events, recaps and stories in your inbox.</p>
              <input type="text" name="company_alt" className="gs-field-x" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Your email"
                  className="w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <div className="relative sm:w-40 shrink-0">
                  <select
                    name="county"
                    required
                    defaultValue=""
                    className="w-full appearance-none text-sm border border-background-300 rounded-md px-3 py-2.5 pr-9 bg-background-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="" disabled>County</option>
                    <option>Los Angeles</option>
                    <option>Orange</option>
                    <option>Ventura</option>
                    <option>San Diego</option>
                  </select>
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-foreground-600">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </div>
              </div>
              <button
                type="submit"
                disabled={status === 'sending'}
                className="mt-2 w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider py-2.5 rounded-md whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending...' : 'Get the digest'}
              </button>
              {status === 'success' && <p className="mt-2.5 text-xs text-primary-600 font-medium">You&apos;re in. Watch your inbox for the next digest.</p>}
              {status === 'error' && <p className="mt-2.5 text-xs text-secondary-700 font-medium">{formError}</p>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}